import { useState, useEffect } from 'react';
import { ComputeMetricDto, DataSourceDto, MetricDto, MetricResultDto, QueryResultDto, VisualizationDto, VisualizationDtoTypeEnum } from '../../../../../../typescript-axios';
import { apiServices } from '../../../../../../services/apiServices';
import { TableVisualization } from './renderers/TableVisualization';
import { LineChartVisualization } from './renderers/LineChartVisualization';
import { Button } from 'primereact/button';
import { AreaChartVisualization } from './renderers/AreaChartVisualization';
import { AreaChartParameters, BarChartParameters, CardParameters, LineChartParameters, TableParameters, VISUALIZATION_CONSTRAINTS } from './config';
import { validateParameters, applyDefaults } from './visualization_parameters_handler';
import { BarChartVisualization } from './renderers/BarChartVisualization';
import { CardVisualization } from './renderers/CardVisualization';


interface UseVisualizationDataResult {
  data: QueryResultDto | MetricResultDto | null;
  loading: boolean;
  error: string | null;
}

export function useVisualizationData(
  visualization: VisualizationDto,
  dataSources: DataSourceDto[],
  metrics: MetricDto[],
): UseVisualizationDataResult {
  const [data, setData] = useState<QueryResultDto | MetricResultDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const constraints = VISUALIZATION_CONSTRAINTS[visualization.type];
    let fetchPromise: Promise<QueryResultDto | MetricResultDto>;

    if (constraints.data_type === 'datasource') {
      const dataSource = dataSources.find(ds => ds.name === visualization.data_source);
      if (!dataSource) {
        setError(`Data source "${visualization.data_source}" not found`);
        setLoading(false);
        return;
      }
      fetchPromise = apiServices.dashboardsApi
        .dashboardsControllerRetrieveDataFromDataSource(dataSource)
        .then(res => res.data);

    } else {
      // data_type === 'metric'
      const metric = metrics.find(m => m.name === visualization.data_source);
      if (!metric) {
        setError(`Metric "${visualization.data_source}" not found`);
        setLoading(false);
        return;
      }
      const computeMetricDto: ComputeMetricDto = {
        aggregate_operation: metric.aggregate_operation,
        query_definition: dataSources.find((item) => item.name === metric.data_source_name).query_definition
      };
      fetchPromise = apiServices.dashboardsApi
        .dashboardsControllerComputeMetric(computeMetricDto)
        .then(res => res.data);
    }

    fetchPromise
      .then(result => { if (!cancelled) setData(result); })
      .catch(err => { if (!cancelled) setError(err.message ?? 'Failed to fetch data'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [visualization.type, visualization.data_source, dataSources, metrics]);

  return { data, loading, error };
}


interface VisualizationRendererProps {
  visualization: VisualizationDto;
  dataSources: DataSourceDto[];
  metrics: MetricDto[];
  isEditMode?: boolean;
  onDelete?: () => void;
}

export function VisualizationRenderer({
  visualization,
  dataSources,
  metrics,
  isEditMode = false,
  onDelete,
}: VisualizationRendererProps) {

  const { data, loading, error } = useVisualizationData(visualization, dataSources, metrics);

  const constraints = VISUALIZATION_CONSTRAINTS[visualization.type];
  const showTopBar = isEditMode || constraints.show_top_bar;

  const renderContent = () => {
    if (loading) return <div className="flex align-items-center justify-content-center h-full text-500">Loading...</div>;
    if (error) return <div className="flex align-items-center justify-content-center h-full text-red-500">{error}</div>;
    if (!data) return null;

    const constraints = VISUALIZATION_CONSTRAINTS[visualization.type];
    const validationErrors = validateParameters(visualization.parameters, constraints.parameterSchema);

    if (validationErrors.length > 0) {
      return (
        <div className="text-red-500">
          <p>Invalid parameters for {visualization.type}:</p>
          <ul>
            {validationErrors.map(e => <li key={e.field}>{e.message}</li>)}
          </ul>
        </div>
      );
    }

    const params = applyDefaults(visualization.parameters, constraints.parameterSchema);

    switch (visualization.type) {
      case VisualizationDtoTypeEnum.Table:
        return <TableVisualization data={data as QueryResultDto} parameters={params as TableParameters} />;
      case VisualizationDtoTypeEnum.LineChart:
        return <LineChartVisualization data={data as QueryResultDto} parameters={params as LineChartParameters} />;
      case VisualizationDtoTypeEnum.AreaChart:
        return <AreaChartVisualization data={data as QueryResultDto} parameters={params as AreaChartParameters} />;
      case VisualizationDtoTypeEnum.BarChart:
        return <BarChartVisualization data={data as QueryResultDto} parameters={params as BarChartParameters} />;
      case VisualizationDtoTypeEnum.Card:
        return <CardVisualization data={data as MetricResultDto} parameters={params as CardParameters} />;
      default:
        return <div className="text-500">Unsupported visualization type: {visualization.type}</div>;
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      overflow: 'hidden',
      background: 'white',
      borderRadius: '8px',
    }}>

      {/* ── Top bar ── */}
      {showTopBar && (
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            padding: '2px 8px',
            borderBottom: '1px solid var(--surface-border)',
            background: '#003257',
            minHeight: '32px',
            position: 'relative',
            borderRadius: '8px 8px 8px 8px',
          }}
        >
          {/* Left: delete button (or empty spacer to keep centering) */}
          <div style={{ width: '1.4rem' }}>
            {isEditMode && (
              <Button
                icon="pi pi-trash"
                rounded
                text
                severity="danger"
                onClick={onDelete}
                style={{ width: '1.4rem', height: '1.4rem', color: 'white' }}
                aria-label="Delete visualization"
              />
            )}
          </div>

          {/* Center: data source name */}
          <span
            style={{
              position: 'absolute',
              left: '50%',
              transform: 'translateX(-50%)',
              fontSize: '0.85rem',
              fontWeight: 'bold',
              color: 'white',
            }}
          >
            {visualization.data_source}
          </span>

          {/* Right: spacer to balance the left button */}
          <div style={{ marginLeft: 'auto', width: '1.4rem' }} />
        </div>
      )}

      {/* ── Content ── */}
      <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
        {renderContent()}
      </div>
    </div>
  );
}